import { cloneElement, useCallback, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import cn from '../../utils/cn';
import useClickOutside from '../../hooks/useClickOutside';
import { useIsMobile } from '../../hooks/useMediaQuery';

export function MenuItem({ icon: Icon, children, destructive, shortcut, onClick, ...rest }) {
  return (
    <button
      type="button"
      role="menuitem"
      className={cn('menu__item', destructive && 'menu__item--danger')}
      onClick={onClick}
      {...rest}
    >
      {Icon && <Icon size={15} />}
      <span className="truncate">{children}</span>
      {shortcut && <kbd>{shortcut}</kbd>}
    </button>
  );
}

export function MenuSeparator() {
  return <div className="menu__sep" role="separator" />;
}

export function MenuLabel({ children }) {
  return <div className="menu__label">{children}</div>;
}

/**
 * Dropdown menu. Renders through a portal so it escapes table overflow, flips when it
 * would fall off-screen, and becomes a bottom sheet on mobile (blueprint §8).
 */
export default function Menu({ trigger, children, align = 'end', width = 208, onOpenChange }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const anchorRef = useRef(null);
  const menuRef = useRef(null);
  const isMobile = useIsMobile();

  const close = useCallback(() => {
    setOpen(false);
    onOpenChange?.(false);
  }, [onOpenChange]);

  useClickOutside([anchorRef, menuRef], close, open);

  useLayoutEffect(() => {
    if (!open || isMobile || !anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    const height = menuRef.current?.offsetHeight || 240;
    const flipUp = rect.bottom + height + 12 > window.innerHeight;
    let left = align === 'end' ? rect.right - width : rect.left;
    left = Math.max(8, Math.min(left, window.innerWidth - width - 8));
    setPos({ top: flipUp ? Math.max(8, rect.top - height - 6) : rect.bottom + 6, left });
  }, [open, align, width, isMobile]);

  const toggle = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setOpen((v) => {
      onOpenChange?.(!v);
      return !v;
    });
  };

  const body = (
    <div role="menu" onClick={(e) => e.stopPropagation()}>
      {typeof children === 'function' ? children(close) : children}
    </div>
  );

  return (
    <>
      {cloneElement(trigger, { ref: anchorRef, onClick: toggle, 'aria-haspopup': 'menu', 'aria-expanded': open })}
      {open &&
        createPortal(
          isMobile ? (
            <div className="scrim scrim--sheet" onMouseDown={(e) => e.target === e.currentTarget && close()}>
              <div className="sheet" ref={menuRef} onClick={close}>
                <div className="sheet__grip" />
                <div style={{ padding: '4px 8px 16px', overflowY: 'auto' }}>{body}</div>
              </div>
            </div>
          ) : (
            <div
              ref={menuRef}
              className="menu"
              style={{ top: pos.top, left: pos.left, width }}
              onClick={close}
            >
              {body}
            </div>
          ),
          document.body,
        )}
    </>
  );
}
